import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Badge, Button, Card, Modal, Radio, RadioGroup, Select, Textarea, TextInput, Tooltip,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import BlueprintEventPicker from "./BlueprintEventPicker";
import { retargetEvent, templateEvent } from "./templateEvent";
import { replaceSpecialChars, smsSegments } from "./smsSegments";
import {
  insertAtCursor, moreMergeFields, senderLabel, starterText,
} from "./smsMessage";
import { hasOptOut, withOptOut } from "../../../../shared/smsOptOut";
import SmsOptOutToggle from "../../../../shared/SmsOptOutToggle";
import { sendTestErrorMessage, sendTestSuccessMessage } from "../../TwilioTemplates/SendTestOverlay";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";

const FIRST_NAME_TAG = "{{fan.first_name}}";

const EVENT_INSERTS = [
  { name: "Event", field: "name" },
  { name: "Date", field: "start_date" },
  { name: "Ticket link", field: "url" },
];

const styles = {
  stack: { display: "flex", flexDirection: "column", gap: 20, marginTop: 24 },
  fieldGroups: { display: "flex", flexDirection: "column", gap: 20 },
  senderNumber: { display: "block", color: "var(--sl-text-primary)" },
  senderName: { display: "block", fontSize: 13, color: "var(--sl-text-tertiary)" },
  label: { fontSize: 14, fontWeight: 600, color: "var(--sl-text-secondary)", marginBottom: 8 },
  row: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 },
  counter: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 13, color: "var(--sl-text-tertiary)" },
  muted: { fontSize: 13, color: "var(--sl-text-tertiary)" },
};

const MessageStep = ({ draft, setDraft, rendered, flushRef }) => {
  const {
    csrfToken, team, broadcast, updateBroadcast, waitForBroadcastSaves, twilioMessagingServices, twilioMergeTags, twilioEventMergeTags, smsStatus, smsStarterTexts, smsStarterPlaceholders, sampleTwilioData,
  } = useBroadcastEditContext();
  const twilioTemplate = broadcast.twilio_template;
  const [serviceId, setServiceId] = useState(
    twilioTemplate.twilio_messaging_service_id || (twilioMessagingServices?.length === 1 ? twilioMessagingServices[0].id : null)
  );
  const [starter, setStarter] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [testNumbers, setTestNumbers] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const textareaId = `sms-message-${broadcast.id}`;
  const mounted = useRef(false);
  const pendingSave = useRef(null);
  const latestUpdateBroadcast = useRef(updateBroadcast);
  latestUpdateBroadcast.current = updateBroadcast;
  const { usesEvent, eventId } = templateEvent(draft);
  const counted = smsSegments(rendered.text ?? draft);
  const special = smsSegments(draft).specialChars;

  useEffect(() => {
    const matchesSaved = draft === (twilioTemplate.template || "") && serviceId === twilioTemplate.twilio_messaging_service_id;
    const firstRun = !mounted.current;
    mounted.current = true;
    if (firstRun && matchesSaved) return undefined;

    const payload = {
      twilio_template_attributes: { id: twilioTemplate.id, template: draft, twilio_messaging_service_id: serviceId },
    };
    pendingSave.current = payload;

    const debounce = setTimeout(() => {
      if (pendingSave.current !== payload) return;
      pendingSave.current = null;
      latestUpdateBroadcast.current(payload, false);
    }, 800);

    return () => clearTimeout(debounce);
  }, [draft, serviceId]);

  const flush = () => {
    const payload = pendingSave.current;
    if (!payload) return;
    pendingSave.current = null;
    latestUpdateBroadcast.current(payload, false);
  };
  if (flushRef) flushRef.current = flush;

  useLayoutEffect(() => () => {
    flush();
    if (flushRef) flushRef.current = null;
  }, []);

  const insert = (tag) => {
    const field = document.getElementById(textareaId);
    const start = field?.selectionStart ?? draft.length;
    const end = field?.selectionEnd ?? draft.length;
    const result = insertAtCursor(draft, tag, start, end);

    setDraft(result.text);
    requestAnimationFrame(() => field?.setSelectionRange(result.cursor, result.cursor));
  };

  const applyStarter = (event) => {
    setDraft(withOptOut(starterText(starter, event, smsStarterPlaceholders), hasOptOut(draft)));
    setStarter(null);
  };

  const fanInserts = [...(twilioMergeTags || [])]
    .sort((a, b) => (b.value === FIRST_NAME_TAG) - (a.value === FIRST_NAME_TAG))
    .slice(0, 4);

  const moreFields = moreMergeFields({
    fanTags: twilioMergeTags,
    eventTags: twilioEventMergeTags,
    shownValues: [...fanInserts.map((tag) => tag.value), ...EVENT_INSERTS.map((tag) => `{{event.${tag.field}}}`)],
    eventId,
  });

  const insertMoreField = (value) => {
    insert(value);
    setShowMoreFields(false);
  };

  const sendTest = () => {
    if (sendingTest) return;
    setSendingTest(true);
    flush();
    waitForBroadcastSaves()
      .then(() => {
        axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
        return axios.post(`/teams/${team.id}/twilio_templates/${twilioTemplate.id}/test`, {
          phone_numbers: testNumbers,
          template: draft,
          liquid_data: sampleTwilioData || {},
        });
      })
      .then((response) => { toastSuccess(sendTestSuccessMessage(response)); setTestOpen(false); })
      .catch((error) => toastError(sendTestErrorMessage(error)))
      .finally(() => setSendingTest(false));
  };

  return (
    <div style={styles.stack}>
      {smsStatus && !smsStatus.can_send && (
        <Alert
          color={smsStatus.cta?.label === "Update Payment Method" ? "error" : "warning"}
          title={smsStatus.reason}
          actions={smsStatus.cta?.label && smsStatus.cta?.path && (
            <Button as="a" href={smsStatus.cta.path} target="_blank" rel="noopener noreferrer" variant="link-color" size="sm">
              {smsStatus.cta.label}
            </Button>
          )}
        />
      )}

      <div>
        <div style={styles.label}>Send from</div>
        <RadioGroup name="messaging-service" value={serviceId} onChange={(value) => setServiceId(value)}>
          {(twilioMessagingServices || []).map((service) => (
            <Radio
              key={service.id}
              value={service.id}
              label={(
                <span style={styles.row}>
                  <span>
                    <span style={styles.senderNumber}>{senderLabel(service)}</span>
                    {senderLabel(service) !== service.name && <span style={styles.senderName}>{service.name}</span>}
                  </span>
                  {service.status && <Badge size="sm" color={service.status === "ready" ? "success" : "gray"}>{service.status}</Badge>}
                </span>
              )}
            />
          ))}
        </RadioGroup>
      </div>

      <div>
        <div style={styles.label}>Start from a common text</div>
        <div style={styles.row}>
          {(smsStarterTexts || []).map((text) => (
            <Button key={text.key} variant="secondary" size="sm" onClick={() => setStarter(text)}>{text.label}</Button>
          ))}
        </div>
        {usesEvent && eventId && (
          <div style={{ marginTop: 12 }}>
            <EventSwitcher teamId={team.id} broadcastId={broadcast.id} eventId={eventId} onChange={(id) => setDraft(retargetEvent(draft, id))} />
          </div>
        )}
      </div>

      <Card>
        <Textarea
          aria-label="Message"
          rows={5}
          value={draft}
          placeholder="Hi {{fan.first_name}}, …"
          id={textareaId}
          onChange={(event) => setDraft(event.target.value)}
        />
        <div style={{ ...styles.row, marginTop: 12 }}>
          <span style={styles.muted}>Insert</span>
          {fanInserts.map((tag) => (
            <Button key={tag.value} variant="secondary" size="sm" onClick={() => insert(tag.value)}>{tag.name}</Button>
          ))}
          {eventId && EVENT_INSERTS.map((tag) => (
            <Button key={tag.field} variant="secondary" size="sm" onClick={() => insert(`{{${eventId}.${tag.field}}}`)}>{tag.name}</Button>
          ))}
          <Button variant="secondary" size="sm" onClick={() => setShowMoreFields(true)}>See more</Button>
        </div>
        <div style={{ ...styles.counter, marginTop: 12 }}>
          <span>{counted.length} characters · {counted.perSegment} per segment</span>
          <Tooltip title="Carriers bill each segment as a separate text.">
            <Badge color={counted.segments > 1 ? "warning" : "gray"}>
              {counted.segments} {counted.segments === 1 ? "segment" : "segments"}
            </Badge>
          </Tooltip>
        </div>
        <div style={{ ...styles.muted, marginTop: 8 }}>Links are shortened and tracked automatically.</div>
      </Card>

      {special.length > 0 && (
        <Alert
          color="warning"
          title={`${special.join(" ")} ${special.length === 1 ? "is a special character" : "are special characters"}, so each segment only holds 70 characters.`}
          actions={<Button variant="secondary" size="sm" onClick={() => setDraft(replaceSpecialChars(draft))}>Replace them</Button>}
        />
      )}

      {rendered.unavailable && (
        <Alert color="warning" title="Couldn't load the preview." description="You can keep editing. Template errors are still checked when the message sends." />
      )}

      {rendered.errors.length > 0 && (
        <Alert color="error" title="Fix these before sending" description={rendered.errors.join(", ")} />
      )}

      <Card>
        <SmsOptOutToggle text={draft} onChange={setDraft} />
      </Card>

      <div>
        <Button variant="secondary" disabled={!draft.trim() || !serviceId} onClick={() => setTestOpen(true)}>Send test text</Button>
      </div>

      {starter && (
        <BlueprintEventPicker
          teamId={team.id}
          broadcastId={broadcast.id}
          blueprint={{ name: starter.label }}
          onApply={(event) => Promise.resolve(applyStarter(event))}
          onApplied={() => {}}
          onSkip={() => applyStarter(null)}
          onClose={() => setStarter(null)}
        />
      )}

      <Modal
        open={showMoreFields}
        onClose={() => setShowMoreFields(false)}
        title="Insert a field"
        description="Adds the field where your cursor is in the message."
        footer={<Button variant="secondary" onClick={() => setShowMoreFields(false)}>Cancel</Button>}
      >
        <div style={styles.fieldGroups}>
          <div>
            <div style={styles.label}>Fan</div>
            <div style={styles.row}>
              {moreFields.fan.map((tag) => (
                <Button key={tag.value} variant="secondary" size="sm" onClick={() => insertMoreField(tag.value)}>{tag.name}</Button>
              ))}
            </div>
          </div>
          <div>
            <div style={styles.label}>Event</div>
            {eventId ? (
              <div style={styles.row}>
                {moreFields.event.map((tag) => (
                  <Button key={tag.value} variant="secondary" size="sm" onClick={() => insertMoreField(tag.value)}>{tag.name}</Button>
                ))}
              </div>
            ) : (
              <div style={styles.muted}>Pick a starter text with an event to add event fields.</div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={testOpen}
        onClose={() => setTestOpen(false)}
        title="Send a test text"
        description="Separate several numbers with commas."
        footer={(
          <>
            <Button variant="secondary" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={sendTest} disabled={sendingTest}>{sendingTest ? "Sending…" : "Send test"}</Button>
          </>
        )}
      >
        <TextInput label="Phone numbers" value={testNumbers} onChange={(event) => setTestNumbers(event.target.value)} placeholder="+15551234567" />
      </Modal>
    </div>
  );
};

const EventSwitcher = ({ teamId, broadcastId, eventId, onChange }) => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`/teams/${teamId}/broadcasts/${broadcastId}/blueprint_events`)
      .then(({ data }) => setEvents(data))
      .catch(() => toastError("Could not load events, please try again"));
  }, [teamId, broadcastId]);

  return <Select label="Event" options={events} value={eventId} onChange={onChange} searchable placeholder="Search events" />;
};

export default MessageStep;
